import { Link, useLocation } from "react-router-dom";
import { List, X, ArrowUpRight } from "@phosphor-icons/react";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { NATUREPULSE_URL, IS_FARMERSPULSE } from "@/lib/site";

/**
 * FarmersStandaloneHeader — Farmers Pulse marketing header.
 *
 * Matches the "Rhythm of Modern Farming" brand system: cream backdrop,
 * forest-green FarmersPulse™ wordmark with a leaf + pulse-line glyph,
 * five-item horizontal nav, and a pill Log In / Dashboard CTA.
 *
 * Modes:
 *   1. Standalone (farmerspulse.com) — basePath="/"
 *   2. Embedded  (naturepulse.net/farmers) — basePath="/farmers"
 */
export default function FarmersStandaloneHeader({ basePath = "/" }) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  const to = (sub) => (basePath === "/" ? (sub === "" ? "/" : sub) : `${basePath}${sub}`);
  const isActive = (sub) => {
    const target = to(sub);
    return pathname === target || pathname.startsWith(target + "/");
  };

  const NAV = [
    { sub: "/markets",  label: "Market Data" },
    { sub: "/crops",    label: "Crop Insights" },
    { sub: "/weather",  label: "Weather" },
    { sub: "/about",    label: "About Us" },
  ];

  return (
    <header
      data-testid="farmers-standalone-header"
      className="fixed top-0 inset-x-0 z-40 bg-[#F8F4E9]/95 backdrop-blur border-b border-[#0F3D2A]/10"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex items-center gap-6">
        <Link
          to={to("")}
          className="flex items-baseline gap-0"
          aria-label="Farmers Pulse home"
          data-testid="farmers-brand-link"
        >
          <FarmersPulseWordmark />
        </Link>

        <nav
          className="ml-auto hidden md:flex items-center gap-8 font-serif-display text-base"
          style={{ color: "#0F3D2A" }}
        >
          {NAV.map((l) => (
            <Link
              key={l.sub}
              to={to(l.sub)}
              data-testid={`farmers-hdr-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`transition ${isActive(l.sub) ? "text-[#2D8659]" : "hover:text-[#2D8659]"}`}
            >
              {l.label}
            </Link>
          ))}
          {user ? (
            <Link
              to={to("/today")}
              data-testid="farmers-hdr-dashboard"
              className="rounded-full border-2 border-[#0F3D2A] text-[#0F3D2A] px-5 py-1.5 font-serif-display hover:bg-[#0F3D2A] hover:text-white transition"
            >
              Dashboard
            </Link>
          ) : (
            <Link
              to="/login"
              data-testid="farmers-hdr-login"
              className="rounded-full border-2 border-[#0F3D2A] text-[#0F3D2A] px-5 py-1.5 font-serif-display hover:bg-[#0F3D2A] hover:text-white transition"
            >
              Log In
            </Link>
          )}
          {/* Sibling site chip — only on the standalone FarmersPulse
              deploy (farmerspulse.com), giving visitors a one-tap link
              back to the flagship NaturePulse. */}
          {IS_FARMERSPULSE && (
            <a
              href={NATUREPULSE_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="farmers-hdr-sibling-np"
              className="hidden lg:inline-flex items-center gap-1 rounded-full px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[#0F3D2A]/70 hover:text-[#0F3D2A] transition"
              title="Sister site — the living planet"
            >
              NaturePulse <ArrowUpRight size={11} weight="bold" />
            </a>
          )}
        </nav>

        <button
          className="md:hidden ml-auto p-2 -mr-2 text-[#0F3D2A]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="farmers-hdr-menu-toggle"
        >
          {open ? <X size={22} weight="thin" /> : <List size={22} weight="thin" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#0F3D2A]/10 bg-[#F8F4E9]">
          <nav className="px-5 py-4 flex flex-col gap-2">
            {NAV.map((l) => (
              <Link
                key={l.sub}
                to={to(l.sub)}
                onClick={() => setOpen(false)}
                className={`py-1.5 font-serif-display text-lg ${
                  isActive(l.sub) ? "text-[#2D8659]" : "text-[#0F3D2A]"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to={user ? to("/today") : "/login"}
              onClick={() => setOpen(false)}
              className="mt-2 w-fit rounded-full border-2 border-[#0F3D2A] text-[#0F3D2A] px-4 py-1.5 font-serif-display"
            >
              {user ? "Dashboard" : "Log In"}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}

/** The FarmersPulse™ wordmark with the small leaf + pulse-line glyph above. */
function FarmersPulseWordmark() {
  return (
    <span className="relative inline-flex items-baseline" style={{ color: "#0F3D2A" }}>
      {/* Glyph — leaf + pulse spike */}
      <svg
        aria-hidden
        viewBox="0 0 100 32"
        className="absolute -top-4 left-1/2 -translate-x-1/2 w-[76px] h-[26px]"
      >
        {/* Leaf */}
        <path
          d="M22 20 C 24 8, 40 4, 46 6 C 42 18, 32 24, 22 20 Z"
          fill="none" stroke="#0F3D2A" strokeWidth="1.5" strokeLinecap="round"
        />
        {/* Leaf midrib */}
        <path d="M28 18 L 44 8" stroke="#0F3D2A" strokeWidth="1.2" fill="none" />
        {/* Pulse line */}
        <path
          d="M46 16 L 54 16 L 58 8 L 62 24 L 66 12 L 70 20 L 78 16 L 92 16"
          fill="none" stroke="#0F3D2A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
        />
      </svg>
      <span className="font-serif-display leading-none text-3xl md:text-4xl tracking-tight">
        Farmers<span className="font-semibold">Pulse</span>
      </span>
      <span className="ml-0.5 font-serif-display text-[10px] md:text-xs align-super">™</span>
    </span>
  );
}
